import { FormHelperText, Stack, Switch, Typography } from '@mui/material';

import CustomLabel from '../CustomLabel';
import Dimens from '../../theme/dimens';
import { FieldProps } from 'formik';
import React from 'react';
import useStyles from './styles';

/**
 * Props for the CustomSwitch component.
 * @extends FieldProps - Inherits Formik's field and form props.
 */
type Props = {
  /** The main label displayed alongside the switch. */
  label?: string;
  /** A secondary label displayed below the main label. */
  subLabel?: string;
  /** When true, the switch is rendered as non-interactive. */
  readOnly?: boolean;
  /** Callback fired when the switch value changes. */
  onChange?: (value: boolean) => void;
} & FieldProps;

/**
 * CustomSwitch Component
 *
 * A reusable Formik-connected switch (toggle) component. It handles:
 * 1. Formik state synchronization (value, touched, errors).
 * 2. Read-only mode rendering for detail/view screens.
 * 3. Optional `onChange` callback for parent-side side effects.
 * 4. Consistent label and error rendering matching other custom form components.
 *
 * @param {Props} props - The component properties.
 * @returns {JSX.Element} The rendered switch component.
 *
 * @example
 * <Field
 *     name='messageTypeSelection'
 *     label={t('messageTypeSelection')}
 *     readOnly={isReadMode}
 *     component={CustomSwitch}
 * />
 */
const CustomSwitch: React.FC<Props> = (props: Props) => {
  const {
    field: { name, onBlur, value },
    form: { errors, touched, setFieldTouched, setFieldValue },
    label,
    subLabel,
    readOnly,
    onChange,
  } = props;
  const styles = useStyles();
  const hasError = Boolean(errors[name] && touched[name]);

  /**
   * Handles the switch toggle event.
   * Updates Formik state and triggers the optional onChange callback.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the switch input.
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setFieldValue(name, newValue);
    setFieldTouched(name, true);
    onChange?.(newValue);
  };

  /**
   * Handles blur to ensure Formik validation triggers
   * when the user interacts with the switch.
   */
  const handleBlur = () => {
    setFieldTouched(name);
    onBlur(name);
  };

  return (
    <Stack sx={styles.wrapper}>
      <Stack sx={styles.row}>
        <CustomLabel label={label} subLabel={subLabel} />
        <Switch
          name={name}
          checked={Boolean(value)}
          sx={styles.iosSwitch}
          disabled={readOnly}
          onChange={handleChange}
          onBlur={handleBlur}
          disableRipple
        />
      </Stack>
      {hasError && (
        <FormHelperText sx={styles.fieldError}>
          {errors[name]?.toString()}
        </FormHelperText>
      )}
    </Stack>
  );
};

export default CustomSwitch;